// SPEC-009 §4.3 — admin auth state shared via React context.
// On boot we attempt /auth/me; if it 401s we know we're logged out (no
// cookie or rotated-out). The context exposes the user, a login mutation
// helper, and a logout helper. Silent refresh is handled inside the api
// client, so this layer only deals with "am I logged in?" state.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { MeResponse } from "@faceheart/api-types";
import { ApiError } from "../api/client";
import { authApi } from "../api/endpoints";

type Status = "loading" | "authed" | "anon";

interface AuthCtx {
  status: Status;
  user: MeResponse | null;
  login: (email: string, password: string, orgCode: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<MeResponse | null>(null);

  const refresh = useCallback(async () => {
    try {
      const u = await authApi.me();
      setUser(u);
      setStatus("authed");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        setStatus("anon");
        return;
      }
      // Network glitch — stay loading so the UI can show a banner / retry.
      console.warn("auth refresh error", err);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Silent re-refresh every 12 min so the 15-min access cookie never expires
  // mid-request (SPEC-009 §4.3 silent refresh).
  useEffect(() => {
    if (status !== "authed") return;
    const id = window.setInterval(() => {
      void authApi.refresh().catch(() => {
        // Refresh failed → next API call will 401 → UI flips to anon.
      });
    }, 12 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [status]);

  const login = useCallback(
    async (email: string, password: string, orgCode: string) => {
      await authApi.login(email, password, orgCode);
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setStatus("anon");
    }
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({ status, user, login, logout, refresh }),
    [status, user, login, logout, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
